import {
  AE_HEADERS,
  AE_COLUMN_PATTERNS,
  INVENTORY_HEADERS,
  ADVERTISER_HEADERS,
  ADVERTISER_COLUMN_PATTERNS,
  FileType,
  PRIMARY_KEY_COLUMNS,
  CATEGORY_COLUMN_PATTERNS,
  TYPE_OF_PURCHASE_COLUMN_PATTERNS,
  CATEGORY_HEADERS,
  TYPE_OF_PURCHASE_HEADERS,
  CONTEXTUAL_INVENTORY_COLUMN_PATTERNS,
  NON_CONTEXTUAL_INVENTORY_COLUMN_PATTERNS,
  CONTEXTUAL_INVENTORY_HEADERS,
  NON_CONTEXTUAL_INVENTORY_HEADERS,
} from '../constants/FileUploadConstants';

export const validateRowData = (
  rowData: string[],
  headers: string[],
  type: string,
  t: (key: string, options?: any) => string
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (rowData.length !== headers.length) {
    errors.push(
      t('row-column-count-mismatch', {
        expected: headers.length,
        actual: rowData.length,
      })
    );
    return { isValid: false, errors };
  }

  let templatePatterns: Record<string, RegExp>;
  if (type === FileType.AE) {
    templatePatterns = AE_COLUMN_PATTERNS;
  } else if (type === FileType.ADVERTISERS) {
    templatePatterns = ADVERTISER_COLUMN_PATTERNS;
  } else if (type === FileType.CATEGORY) {
    templatePatterns = CATEGORY_COLUMN_PATTERNS;
  } else if (type === FileType.TYPE_OF_PURCHASE) {
    templatePatterns = TYPE_OF_PURCHASE_COLUMN_PATTERNS;
  } else if (type === FileType.CONTEXTUAL_INVENTORY) {
    templatePatterns = CONTEXTUAL_INVENTORY_COLUMN_PATTERNS;
  } else if (type === FileType.NON_CONTEXTUAL_INVENTORY) {
    templatePatterns = NON_CONTEXTUAL_INVENTORY_COLUMN_PATTERNS;
  }

  headers.forEach((header, index) => {
    const cellValue = String(rowData[index] || '').trim();
    const pattern = templatePatterns[header as keyof typeof templatePatterns];

    if (!cellValue) {
      errors.push(t('header-missing-value', { missingHeader: header }));
    } else if (pattern && !pattern.test(cellValue)) {
      errors.push(
        t('header-invalid-value', {
          invalidHeader: header,
          invalidCellValue: cellValue,
        })
      );
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validateAllRows = (
  fileData: string[][],
  type: string,
  t: (key: string, options?: any) => string
): {
  isValid: boolean;
  rowErrors: Array<{ row: number; errors: string[] }>;
  uniquenessErrors?: string[];
  duplicateGroups?: number[][];
} => {
  const headers = fileData[0];
  let dataRows = fileData.slice(1);

  while (dataRows.length > 0 && dataRows[dataRows.length - 1].every(cell => String(cell || '').trim() === '')) {
    dataRows.pop();
  }

  if (dataRows.length === 0) {
    return { isValid: true, rowErrors: [], uniquenessErrors: undefined, duplicateGroups: [] };
  }
  const rowErrors: Array<{ row: number; errors: string[] }> = [];

  // Check primary key uniqueness
  const uniquenessValidation = validateNoDuplicatesInPrimaryKey(
    [headers, ...dataRows],
    type,
    t
  );

  dataRows.forEach((row, index) => {
    const validation = validateRowData(row, headers, type, t);
    if (!validation.isValid) {
      rowErrors.push({
        row: index + 1,
        errors: validation.errors,
      });
    }
  });

  return {
    isValid: rowErrors.length === 0 && uniquenessValidation.isValid,
    rowErrors,
    uniquenessErrors: uniquenessValidation.isValid
      ? undefined
      : uniquenessValidation.errors,
    duplicateGroups: uniquenessValidation.duplicateGroups,
  };
};

export const validateNoDuplicatesInPrimaryKey = (
  fileData: string[][],
  type: string,
  t: (key: string, options?: any) => string
): { isValid: boolean; errors: string[]; duplicateGroups: number[][] } => {
  const headers = fileData[0];
  const primaryKeyColumn =
    PRIMARY_KEY_COLUMNS[type as keyof typeof PRIMARY_KEY_COLUMNS];

  // Handle both single column and composite key (array of columns)
  const isCompositeKey = Array.isArray(primaryKeyColumn);
  const primaryKeyColumns = isCompositeKey
    ? primaryKeyColumn
    : [primaryKeyColumn];

  // Get indices for all primary key columns
  const primaryKeyIndices = primaryKeyColumns.map((col) =>
    headers.indexOf(col)
  );

  const dataRows = fileData.slice(1); // Skip header row
  const primaryKeyValues = dataRows.map((row, index) => ({
    // For composite key, join values with "|" separator
    value: primaryKeyIndices
      .map((idx) => String(row[idx] || '').trim())
      .join('|'),
    rowIndex: index + 1,
  }));

  const duplicates = new Map<string, number[]>();

  primaryKeyValues.forEach(({ value, rowIndex }) => {
    if (value) {
      if (!duplicates.has(value)) {
        duplicates.set(value, []);
      }
      const existingRows = duplicates.get(value);
      if (existingRows) {
        existingRows.push(rowIndex);
      }
    }
  });

  const duplicateEntries = Array.from(duplicates.entries()).filter(
    ([, rows]) => rows.length > 1
  );

  if (duplicateEntries.length > 0) {
    const columnName = isCompositeKey
      ? primaryKeyColumns.join(' + ')
      : primaryKeyColumn;

    // Generate error messages for ALL duplicate groups
    const errors = duplicateEntries.map(([, rows]) =>
      t('primary-key-duplicate-error', {
        columnName: columnName,
        duplicateRowNumbers: rows,
      })
    );

    // Extract all duplicate groups as arrays of row numbers
    const duplicateGroups = duplicateEntries.map(([, rows]) => rows);

    return {
      isValid: false,
      errors,
      duplicateGroups,
    };
  }

  return { isValid: true, errors: [], duplicateGroups: [] };
};

export const validateContentHeaders = (
  headers: string[],
  type: string,
  t: (key: string, options?: any) => string
): [error: string, success: string] => {
  let templateHeaders: string[];
  if (type === FileType.AE) {
    templateHeaders = AE_HEADERS;
  } else if (type === FileType.ADVERTISERS) {
    templateHeaders = ADVERTISER_HEADERS;
  } else if (type === FileType.CATEGORY) {
    templateHeaders = CATEGORY_HEADERS;
  } else if (type === FileType.TYPE_OF_PURCHASE) {
    templateHeaders = TYPE_OF_PURCHASE_HEADERS;
  } else if (type === FileType.CONTEXTUAL_INVENTORY) {
    templateHeaders = CONTEXTUAL_INVENTORY_HEADERS;
  } else if (type === FileType.NON_CONTEXTUAL_INVENTORY) {
    templateHeaders = NON_CONTEXTUAL_INVENTORY_HEADERS;
  } else {
    templateHeaders = INVENTORY_HEADERS;
  }
  if (headers.length < templateHeaders.length) {
    return [t('missing-headers-message'), ''];
  } else if (headers.length > templateHeaders.length) {
    return [t('excessive-duplicate-headers-message'), ''];
  } else {
    for (let index = 0; index < headers.length; index++) {
      if (headers[index] !== templateHeaders[index]) {
        return [
          t('column-incorrect-order-spelling-message', {
            incorrectHeader: headers[index],
          }),
          '',
        ];
      }
    }
  }
  return ['', t('file-valid-message')];
};
